import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAvailabilityWatchActions } from '../hooks/useAvailabilityWatch';
import { messages } from '../messages';

const LOGIN_TOAST_DURATION_MS = 10_000;

interface Props {
  professionalId: string;
  professionalName: string;
  isWatching: boolean;
  variant?: 'full' | 'icon';
  className?: string;
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

export function NotifyWhenOnlineButton({
  professionalId,
  professionalName,
  isWatching,
  variant = 'full',
  className,
}: Props) {
  const { createMutation, deleteMutation } = useAvailabilityWatchActions();
  const { isAuthenticated, openLoginModal } = useAuth();
  const loginPromptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (loginPromptTimerRef.current) clearTimeout(loginPromptTimerRef.current);
    };
  }, []);

  const busy = createMutation.isPending || deleteMutation.isPending;
  const disabled = busy || !isOnline;

  const handleClick = () => {
    if (disabled) return;
    if (!isAuthenticated) {
      // Inform user, then open the login modal once the toast has had its
      // moment so the message is read before the modal grabs focus.
      toast.warning(
        `Devi essere loggato per essere avvisato quando ${professionalName} sarà online.`,
        { duration: LOGIN_TOAST_DURATION_MS },
      );
      if (loginPromptTimerRef.current) clearTimeout(loginPromptTimerRef.current);
      loginPromptTimerRef.current = setTimeout(() => {
        openLoginModal();
      }, LOGIN_TOAST_DURATION_MS);
      return;
    }
    if (isWatching) deleteMutation.mutate(professionalId);
    else createMutation.mutate(professionalId);
  };

  const iconButtonClass = isWatching
    ? 'bg-brand-orange/10 text-brand-orange font-semibold border border-brand-orange/30 hover:bg-brand-orange/20'
    : 'bg-white border border-brand-orange text-brand-orange font-semibold hover:bg-brand-orange/5';

  const fullButtonClass = [
    'inline-flex items-center justify-center gap-2 px-[18px] py-[10px]',
    'border-[1.5px] border-[#F26B2C] bg-[#F26B2C]/[0.06]',
    'text-[#F26B2C] text-[13px] font-medium tracking-[0.2px]',
    'rounded cursor-pointer text-center leading-tight',
    'transition hover:bg-[#F26B2C]/[0.12] active:scale-[0.98]',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F26B2C]/40',
    'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
  ].join(' ');

  const ariaLabel = isWatching
    ? messages.ariaLabelActive(professionalName)
    : messages.ariaLabelIdle(professionalName);

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-pressed={isWatching}
        aria-busy={busy}
        aria-label={ariaLabel}
        title={!isOnline ? messages.offlineDisabled : ariaLabel}
        className={`inline-flex h-11 w-11 items-center justify-center rounded-full transition disabled:opacity-50 ${iconButtonClass} ${className ?? ''}`}
      >
        <BellIcon className="h-5 w-5" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={isWatching}
      aria-busy={busy}
      aria-label={ariaLabel}
      title={!isOnline ? messages.offlineDisabled : undefined}
      className={`${fullButtonClass} ${className ?? ''}`}
    >
      <BellIcon className="h-[15px] w-[15px] shrink-0" />
      <span className="min-w-0">{isWatching ? messages.ctaActive : messages.ctaIdle}</span>
    </button>
  );
}
